/**
 * useAcknowledgeOrder Hook
 * 
 * Handles first-acknowledgement of orders using atomic RPC function.
 * Sets acknowledged_at timestamp and logs "First acknowledged" entry.
 * 
 * Features:
 * - Atomic operation (race-safe, no select-before-update)
 * - Optimistic UI update (removes NEW badge instantly)
 * - Idempotent (safe to call multiple times)
 */

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

export type AcknowledgeReason = 
  | "detail_open" 
  | "status_change" 
  | "session_assigned" 
  | "note_added" 
  | "marked_paid";

export function useAcknowledgeOrder() {
  const queryClient = useQueryClient();
  const { language } = useLanguage();

  /**
   * Acknowledge an order if not already acknowledged.
   * Uses atomic RPC function to prevent races and ensure single log entry.
   * 
   * @param orderId - The order to acknowledge
   * @param reason - Why the order was acknowledged (for log entry)
   * @returns true if this was the first acknowledgement, false if already done
   */
  const acknowledgeOrder = useCallback(
    async (orderId: string, reason: AcknowledgeReason): Promise<boolean> => {
      if (!orderId) return false;

      // Optimistic update: immediately mark as acknowledged in cache
      queryClient.setQueryData(["orders"], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data?.map((order: any) =>
              order.id === orderId
                ? { ...order, acknowledged_at: new Date().toISOString() }
                : order
            ),
          })),
        };
      });

      // Also update flat array format (used by usePaginatedOrders)
      queryClient.setQueriesData({ queryKey: ["orders"] }, (oldData: any) => {
        if (Array.isArray(oldData)) {
          return oldData.map((order: any) =>
            order.id === orderId
              ? { ...order, acknowledged_at: new Date().toISOString() }
              : order
          );
        }
        return oldData;
      });

      // Update single order cache
      queryClient.setQueryData(["order", orderId], (oldOrder: any) => {
        if (!oldOrder) return oldOrder;
        return { ...oldOrder, acknowledged_at: new Date().toISOString() };
      });

      // Update today-orders for TodayBoard instant sync
      queryClient.setQueriesData({ queryKey: ["today-orders"] }, (oldData: any) => {
        if (Array.isArray(oldData)) {
          return oldData.map((order: any) =>
            order.id === orderId
              ? { ...order, acknowledged_at: new Date().toISOString() }
              : order
          );
        }
        return oldData;
      });

      try {
        // Call atomic RPC function
        const lang = language === "ka" ? "ka" : "en";
        const { data, error } = await supabase.rpc("acknowledge_order_once", {
          _order_id: orderId,
          _reason: reason,
          _lang: lang,
        });

        if (error) {
          console.error("Failed to acknowledge order:", error);
          // Revert optimistic update on error
          queryClient.invalidateQueries({ queryKey: ["orders"] });
          return false;
        }

        // data is true if this was the first acknowledgement
        if (data === true) {
          // Ensure ALL order queries reflect the change consistently
          queryClient.invalidateQueries({ queryKey: ["orders"] });
          queryClient.invalidateQueries({ queryKey: ["today-orders"] });
        }
        return data === true;
      } catch (error) {
        console.error("Error in acknowledgeOrder:", error);
        queryClient.invalidateQueries({ queryKey: ["orders"] });
        return false;
      }
    },
    [queryClient, language]
  );

  return { acknowledgeOrder };
}
